import React, {useEffect, useRef, useState} from 'react';
import {CButton, CInput, CSelect, CTextarea} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {getApi} from "../../apis/backendApis";
import {
    Anexo,
    EtapaProcesso,
    Evento,
    Historico,
    ItemProcesso,
    PapelProcesso,
    Processo,
    RegraObservacao,
    SituacaoItem,
    SituacaoItemList,
    TipoItem,
    SituacaoEvento,
    dataBr,
    proximoAgendado,
    quandoEvento,
    resolvido,
    tamanhoArquivo,
} from "./modelo";
import {Executar} from "./processoTela";
import {baixarAnexo} from "./abas";
import {COR_EVENTO} from "./calendario";

interface Props {
    processo: Processo,
    etapa: EtapaProcesso,
    item: ItemProcesso,
    papeis: Map<number, PapelProcesso>,
    usuarios: Array<{id: number, nome: string}>,
    ativo: boolean,
    ehCliente: boolean,
    versao: number,
    executar: Executar,
    /** Recarrega o processo e o historico (movimento que nao passa por executar). */
    recarregar: () => void,
    onFechar: () => void,
    /** Desktop: coluna fixa da tela, sem fundo escuro por tras. */
    embutido?: boolean,
    /** Gestor ou responsavel pela etapa: muda o prazo. */
    podeReprogramar?: boolean,
    /** Agenda do item (#484909): os eventos dele e o "Agendar". */
    eventos?: Evento[],
    podeAgendar?: boolean,
    onAgendar?: () => void,
    onAbrirEvento?: (evento: Evento) => void,
}

type Modo = null | 'declinar' | 'cliente' | 'transferir' | 'reabrir' | 'reprogramar';

const textoResposta = (item: ItemProcesso): string => {
    const r = item.resposta;
    if (r === null || r === undefined) return item.tipo === TipoItem.ARQUIVO ? 'Arquivo anexado' : '';
    switch (item.tipo) {
        case TipoItem.CHECK: return 'Feito';
        case TipoItem.SELECAO: return item.opcoes.find(o => o.valor === r)?.rotulo ?? String(r);
        case TipoItem.SELECAO_MULTIPLA: return (r as string[]).map(v => item.opcoes.find(o => o.valor === v)?.rotulo ?? v).join(', ');
        case TipoItem.DATA: return dataBr(r);
        default: return String(r);
    }
};

/**
 * Painel do item: no celular cobre a tela (abre de baixo), no desktop e uma
 * gaveta a direita. Responder, declinar, pedir ao cliente, transferir,
 * reabrir, anexar e comentar.
 */
const ItemPainel = ({processo, etapa, item, papeis, usuarios, ativo, ehCliente, versao, executar, recarregar, onFechar, embutido = false, podeReprogramar = false,
                        eventos = [], podeAgendar = false, onAgendar, onAbrirEvento}: Props) => {
    const [cascata, setCascata] = useState(false);
    const [resposta, setResposta] = useState<any>(item.tipo === TipoItem.SELECAO_MULTIPLA ? [] : (item.tipo === TipoItem.CHECK ? false : ''));
    const [observacao, setObservacao] = useState('');
    const [peloCliente, setPeloCliente] = useState(ehCliente);
    const [modo, setModo] = useState<Modo>(null);
    const [texto, setTexto] = useState('');
    const [destino, setDestino] = useState('');
    const [comentario, setComentario] = useState('');
    const [comentarioCliente, setComentarioCliente] = useState(false);
    const [respostaDuvida, setRespostaDuvida] = useState('');
    const [anexos, setAnexos] = useState<Anexo[]>([]);
    const [historico, setHistorico] = useState<Historico[]>([]);
    const arquivoRef = useRef<HTMLInputElement>(null);

    const papel = papeis.get(item.papel_id);
    const pode = ativo && item.pode_executar;
    const feito = resolvido(item);
    const agendado = proximoAgendado(eventos);

    // Trocou de item: limpa o formulario. Em revisao, ja vem com o que o cliente mandou.
    useEffect(() => {
        const vazio = item.tipo === TipoItem.SELECAO_MULTIPLA ? [] : (item.tipo === TipoItem.CHECK ? false : '');
        setResposta(item.em_revisao && item.resposta !== null ? item.resposta : vazio);
        setObservacao(item.em_revisao ? (item.observacao ?? '') : '');
        setPeloCliente(ehCliente);
        setModo(null);
    }, [item.id, item.em_revisao]);

    useEffect(() => {
        const api = getApi();
        api.get(`processo/anexos/${processo.id}`, {params: {item_id: item.id}}).then(r => setAnexos(r.data)).catch(() => setAnexos([]));
        api.get(`processo/historico/${processo.id}`, {params: {item_id: item.id}}).then(r => setHistorico(r.data)).catch(() => setHistorico([]));
    }, [item.id, versao, item.situacao, item.anexos]);

    // Esc fecha; a pagina por tras nao rola enquanto o painel esta aberto no celular.
    useEffect(() => {
        const tecla = (e: KeyboardEvent) => e.key === 'Escape' && onFechar();
        window.addEventListener('keydown', tecla);
        if (!embutido) {
            document.body.classList.add('pr-painel-aberto');
        }
        return () => {
            window.removeEventListener('keydown', tecla);
            document.body.classList.remove('pr-painel-aberto');
        };
    }, []);

    const escolhida = (valor: string) => (Array.isArray(resposta) ? resposta.includes(valor) : resposta === valor);
    const observacaoObrigatoria = item.observacao_regra === RegraObservacao.OBRIGATORIA
        || (item.observacao_regra === RegraObservacao.CONFORME_OPCAO && item.opcoes.some(o => o.exige_observacao && escolhida(o.valor)));

    const concluir = () => executar(`processo/item/responder/${item.id}`, {resposta, observacao, respondido_pelo_cliente: peloCliente});

    const confirmaModo = () => {
        const fim = (ok: boolean) => ok && (setModo(null), setTexto(''), setDestino(''));
        if (modo === 'declinar') {
            executar(`processo/item/declinar/${item.id}`, {justificativa: texto}).then(fim);
        } else if (modo === 'cliente') {
            executar(`processo/item/situacao/${item.id}`, {situacao: SituacaoItem.AGUARDANDO_CLIENTE, mensagem: texto}).then(fim);
        } else if (modo === 'reabrir') {
            executar(`processo/item/reabrir/${item.id}`, {motivo: texto}).then(fim);
        } else if (modo === 'reprogramar') {
            executar(`processo/item/reprogramar/${item.id}`, {prazo: destino, motivo: texto, cascata}).then(fim);
        } else if (modo === 'transferir') {
            const [tipo, idDestino] = destino.split(':');
            executar(`processo/item/transferir/${item.id}`, {[tipo === 'p' ? 'papel_id' : 'usuario_id']: Number(idDestino), motivo: texto}).then(fim);
        }
    };

    const abreModo = (novo: Modo) => {
        setModo(novo);
        // Devolver ao cliente pede o que faltou, nao repete o pedido antigo.
        setTexto(novo === 'cliente' && !item.em_revisao ? (item.acao_cliente || '') : '');
        setDestino(novo === 'reprogramar' ? item.prazo : '');
        setCascata(false);
    };

    const enviarArquivos = (lista: FileList | null) => {
        if (!lista || lista.length === 0) return;
        executar(`processo/anexo/${processo.id}`, {item_id: item.id, enviado_pelo_cliente: peloCliente && ehCliente}, Array.from(lista))
            .then(() => { if (arquivoRef.current) arquivoRef.current.value = ''; });
    };

    const removerAnexo = (anexo: Anexo) => {
        if (!window.confirm(`Remover ${anexo.nome}?`)) return;
        getApi().delete(`processo/anexo/${anexo.id}`)
            .then(recarregar)
            .catch(e => window.alert(e?.response?.data?.error ?? 'Não foi possível remover.'));
    };

    // Responder a duvida e um comentario visivel ao cliente; "resolvida" e um comentario interno.
    // Qualquer comentario da equipe no item tira a duvida do destaque.
    const responderDuvida = () => {
        executar(`processo/comentario/${processo.id}`, {mensagem: respostaDuvida, item_id: item.id, visivel_cliente: true})
            .then(ok => ok && setRespostaDuvida(''));
    };
    const duvidaResolvida = () => {
        executar(`processo/comentario/${processo.id}`, {mensagem: 'Dúvida do cliente resolvida fora do portal.', item_id: item.id, visivel_cliente: false});
    };

    const comentar = () => {
        executar(`processo/comentario/${processo.id}`, {mensagem: comentario, item_id: item.id, visivel_cliente: comentarioCliente})
            .then(ok => ok && setComentario(''));
    };

    const renderResposta = () => {
        switch (item.tipo) {
            case TipoItem.CHECK:
                return (
                    <label className="pr-opcao">
                        <input type="checkbox" checked={!!resposta} onChange={e => setResposta(e.target.checked)}/> Feito
                    </label>
                );
            case TipoItem.SELECAO:
                return item.opcoes.map(opcao => (
                    <label key={opcao.valor} className="pr-opcao">
                        <input type="radio" name={`item-${item.id}`} checked={resposta === opcao.valor} onChange={() => setResposta(opcao.valor)}/>
                        {opcao.rotulo}{opcao.exige_observacao && <em> (pede observação)</em>}
                    </label>
                ));
            case TipoItem.SELECAO_MULTIPLA:
                return item.opcoes.map(opcao => (
                    <label key={opcao.valor} className="pr-opcao">
                        <input type="checkbox" checked={escolhida(opcao.valor)}
                               onChange={e => setResposta(e.target.checked ? [...resposta, opcao.valor] : resposta.filter((v: string) => v !== opcao.valor))}/>
                        {opcao.rotulo}
                    </label>
                ));
            case TipoItem.TEXTO:
                return <CTextarea rows={4} value={resposta} onChange={(e: any) => setResposta(e.target.value)} placeholder="Resposta"/>;
            case TipoItem.DATA:
                return <CInput type="date" value={resposta} onChange={(e: any) => setResposta(e.target.value)}/>;
            case TipoItem.ARQUIVO:
                return <p className="pr-ajuda">Este item se conclui com o arquivo: anexe abaixo e depois conclua.</p>;
        }
        return null;
    };

    return (
        <>
            {!embutido && <div className="pr-painel-fundo" onClick={onFechar}/>}
            <aside className={`pr-painel ${embutido ? 'embutido' : ''}`} role={embutido ? 'region' : 'dialog'} aria-label={item.titulo}>
                <header className="pr-painel-cab">
                    <div>
                        <span className="pr-painel-etapa">Etapa {etapa.ordem} · {etapa.nome}{item.grupo_nome ? ` · ${item.grupo_nome}` : ''}</span>
                        <h2>{item.titulo}</h2>
                    </div>
                    <button type="button" className="pr-fechar" onClick={onFechar} title={embutido ? 'Voltar ao resumo da etapa' : 'Fechar'}><CIcon name="cil-x"/></button>
                </header>

                <div className="pr-painel-corpo">
                    <div className="pr-painel-info">
                        <span className={`pr-chip sit-${item.situacao}`}>{SituacaoItemList.getDescription(item.situacao)}</span>
                        {!feito && (
                            <span className={`pr-chip ${item.atrasado ? 'atrasado' : item.vencendo ? 'vencendo' : ''}`}>
                                prazo {dataBr(item.prazo)}{item.prazo !== item.prazo_original ? ` (meta ${dataBr(item.prazo_original)})` : ''}
                            </span>
                        )}
                        <span className="pr-chip">{papel?.pessoa || 'sem responsável'} · {papel?.codigo}</span>
                        {item.modulo_nome && <span className="pr-chip">{item.modulo_nome}</span>}
                        {!item.visivel_cliente && <span className="pr-chip">interno</span>}
                        {agendado && <span className="pr-chip agendado"><CIcon name="cil-calendar"/> agendado {quandoEvento(agendado.inicio)}</span>}
                    </div>

                    {item.duvidas.length > 0 && (
                        <section className="pr-bloco pr-duvida" aria-label="Dúvida do cliente">
                            <div className="pr-duvida-titulo">
                                <CIcon name="cil-speech"/> {item.duvidas.length === 1 ? 'O cliente tem uma dúvida' : `O cliente tem ${item.duvidas.length} dúvidas`}
                            </div>
                            {item.duvidas.map(d => (
                                <blockquote key={d.id} className="pr-duvida-mensagem">
                                    <p>{d.mensagem}</p>
                                    <footer>{d.autor} · {dataBr(d.quando)} · pelo portal</footer>
                                </blockquote>
                            ))}
                            <CTextarea rows={3} value={respostaDuvida} onChange={(e: any) => setRespostaDuvida(e.target.value)}
                                       placeholder="Sua resposta (o cliente vê no portal, no histórico da implantação)"/>
                            <div className="pr-duvida-acoes">
                                <button type="button" className="pr-link" onClick={duvidaResolvida}
                                        title="Já respondeu por telefone, e-mail ou reunião: tira do destaque sem mandar nada ao cliente">
                                    Já resolvi por outro canal
                                </button>
                                <CButton className="pr-duvida-botao" disabled={!respostaDuvida.trim()} onClick={responderDuvida}>
                                    <CIcon name="cil-paper-plane"/> Responder ao cliente
                                </CButton>
                            </div>
                        </section>
                    )}

                    {item.orientacao && <p className="pr-orientacao">{item.orientacao}</p>}
                    {item.acao_cliente && (
                        <div className="pr-pedido"><CIcon name="cil-user"/> <span><strong>Pedido ao cliente:</strong> {item.acao_cliente}</span></div>
                    )}

                    {feito && (
                        <section className="pr-bloco pr-respondido">
                            <div className="pr-bloco-titulo">{item.situacao === SituacaoItem.DECLINADO ? 'Declinado' : 'Resposta'}</div>
                            {item.situacao === SituacaoItem.CONCLUIDO && <p className="pr-resposta">{textoResposta(item)}</p>}
                            {item.observacao && <p>{item.observacao}</p>}
                            {item.justificativa_declinio && <p>{item.justificativa_declinio}</p>}
                            <small>{item.respondido_por}{item.respondido_pelo_cliente ? (item.respondido_por ? ' (resposta do cliente)' : 'Cliente') : ''} · {dataBr(item.respondido_em)}</small>
                            {pode && modo !== 'reabrir' && (
                                <div><CButton size="sm" color="dark" variant="outline" onClick={() => abreModo('reabrir')}>Reabrir</CButton></div>
                            )}
                        </section>
                    )}

                    {item.em_revisao && (
                        <section className="pr-bloco pr-revisao">
                            <div className="pr-bloco-titulo">Resposta do cliente pelo portal</div>
                            {textoResposta(item) && <p className="pr-resposta">{textoResposta(item)}</p>}
                            {item.observacao && <p>{item.observacao}</p>}
                            {!textoResposta(item) && !item.observacao && <p>O cliente enviou arquivo: veja em Anexos.</p>}
                            <small>{dataBr(item.respondido_em)} · confira, ajuste se precisar e conclua, ou devolva ao cliente.</small>
                        </section>
                    )}

                    {!feito && pode && modo === null && (
                        <section className="pr-bloco">
                            <div className="pr-bloco-titulo">{item.em_revisao ? 'Revisar e concluir' : 'Responder'}</div>
                            <div className="pr-resposta-campos">{renderResposta()}</div>
                            {item.observacao_regra !== RegraObservacao.NAO && (
                                <CTextarea rows={3} value={observacao} onChange={(e: any) => setObservacao(e.target.value)}
                                           placeholder={observacaoObrigatoria ? 'Observação (obrigatória)' : 'Observação (opcional)'}
                                           className={observacaoObrigatoria && !observacao.trim() ? 'pr-campo-obrigatorio' : ''}/>
                            )}
                            {ehCliente && !item.em_revisao && (
                                <label className="pr-opcao pequena">
                                    <input type="checkbox" checked={peloCliente} onChange={e => setPeloCliente(e.target.checked)}/> Registrado em nome do cliente
                                </label>
                            )}
                            <CButton color="success" className="pr-botao-principal" onClick={concluir}>
                                <CIcon name="cil-check"/> {item.em_revisao ? 'Aprovar e concluir' : 'Concluir'}
                            </CButton>
                            <div className="pr-acoes-secundarias">
                                {item.situacao !== SituacaoItem.EM_ANDAMENTO && (
                                    <button type="button" onClick={() => executar(`processo/item/situacao/${item.id}`, {situacao: SituacaoItem.EM_ANDAMENTO})}>Em andamento</button>
                                )}
                                {item.situacao !== SituacaoItem.AGUARDANDO_CLIENTE && (
                                    <button type="button" onClick={() => abreModo('cliente')}>{item.em_revisao ? 'Devolver ao cliente' : 'Solicitar ao cliente'}</button>
                                )}
                                {item.situacao !== SituacaoItem.PENDENTE && (
                                    <button type="button" onClick={() => executar(`processo/item/situacao/${item.id}`, {situacao: SituacaoItem.PENDENTE})}>Voltar a pendente</button>
                                )}
                                <button type="button" onClick={() => abreModo('transferir')}>Transferir</button>
                                {podeReprogramar && <button type="button" onClick={() => abreModo('reprogramar')}>Reprogramar prazo</button>}
                                {item.permite_declinar && <button type="button" className="perigo" onClick={() => abreModo('declinar')}>Declinar</button>}
                            </div>
                        </section>
                    )}

                    {modo !== null && (
                        <section className="pr-bloco pr-modo">
                            <div className="pr-bloco-titulo">
                                {{declinar: 'Declinar o item', cliente: item.em_revisao ? 'Devolver ao cliente' : 'Solicitar ao cliente', transferir: 'Transferir o item', reabrir: 'Reabrir o item', reprogramar: 'Reprogramar o prazo'}[modo]}
                            </div>
                            {modo === 'transferir' && (
                                <CSelect value={destino} onChange={(e: any) => setDestino(e.target.value)}>
                                    <option value="">Para quem?</option>
                                    <optgroup label="Papéis do processo">
                                        {Array.from(papeis.values()).filter(p => p.id !== item.papel_id).map(p => (
                                            <option key={`p${p.id}`} value={`p:${p.id}`}>{p.nome}{p.pessoa ? ` — ${p.pessoa}` : ''}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="Direto para uma pessoa">
                                        {usuarios.map(u => <option key={`u${u.id}`} value={`u:${u.id}`}>{u.nome}</option>)}
                                    </optgroup>
                                </CSelect>
                            )}
                            {modo === 'reprogramar' && (
                                <>
                                    <CInput type="date" value={destino} onChange={(e: any) => setDestino(e.target.value)}/>
                                    <p className="pr-ajuda">Meta original: {dataBr(item.prazo_original)}. Fim de semana e feriado passam para o próximo dia útil.</p>
                                    <label className="pr-opcao pequena">
                                        <input type="checkbox" checked={cascata} onChange={e => setCascata(e.target.checked)}/> Se passar do fim da etapa, empurrar as etapas seguintes
                                    </label>
                                </>
                            )}
                            <CTextarea rows={3} value={texto} onChange={(e: any) => setTexto(e.target.value)}
                                       placeholder={{declinar: 'Justificativa (obrigatória)', cliente: item.em_revisao ? 'O que faltou ou precisa corrigir (o cliente recebe por e-mail)' : 'O que o cliente precisa enviar ou fazer', transferir: 'Motivo (obrigatório)', reabrir: 'Motivo (obrigatório)', reprogramar: 'Motivo (obrigatório)'}[modo]}/>
                            <div className="pr-acoes-modo">
                                <CButton color="secondary" variant="outline" onClick={() => setModo(null)}>Cancelar</CButton>
                                <CButton color={modo === 'declinar' ? 'danger' : 'primary'} onClick={confirmaModo}
                                         disabled={!texto.trim() || ((modo === 'transferir' || modo === 'reprogramar') && !destino)}>Confirmar</CButton>
                            </div>
                        </section>
                    )}

                    {!pode && !feito && (
                        <p className="pr-ajuda">
                            {ativo ? 'Só o responsável pelo item, o responsável pela etapa ou o gestor respondem este item.' : `Processo ${processo.situacao_nome.toLowerCase()}: não aceita movimentos.`}
                        </p>
                    )}

                    {(eventos.length > 0 || (podeAgendar && !feito)) && (
                        <section className="pr-bloco pr-item-agenda" aria-label="Agenda do item">
                            <div className="pr-bloco-titulo">Agenda</div>
                            {eventos.length === 0 && <p className="pr-ajuda">Nada agendado para este item.</p>}
                            {eventos.map(evento => (
                                <button type="button" key={evento.id} className={`pr-agenda-evento sit-evento-${evento.situacao} ${evento.situacao !== SituacaoEvento.AGENDADO ? 'passado' : ''}`}
                                        onClick={() => onAbrirEvento && onAbrirEvento(evento)}>
                                    <span className="pr-agenda-cor" style={{background: COR_EVENTO[evento.tipo]}}/>
                                    <span>
                                        <strong>{evento.titulo}</strong>
                                        <em>{evento.tipo_nome} · {quandoEvento(evento.inicio)}{evento.fim ? `–${evento.fim.slice(11, 16)}` : ''} · {evento.situacao_nome}</em>
                                    </span>
                                </button>
                            ))}
                            {podeAgendar && !feito && onAgendar && (
                                <CButton size="sm" color="primary" variant="outline" className="pr-item-agendar" onClick={onAgendar}>
                                    <CIcon name="cil-calendar"/> {agendado ? 'Agendar outro' : 'Agendar'}
                                </CButton>
                            )}
                        </section>
                    )}

                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Anexos {item.exige_anexo && <em>(obrigatório)</em>}</div>
                        {anexos.length === 0 && <p className="pr-ajuda">Nenhum anexo.</p>}
                        {anexos.map(anexo => (
                            <div key={anexo.id} className="pr-anexo">
                                <CIcon name="cil-paperclip"/>
                                <button type="button" className="pr-link" onClick={() => baixarAnexo(anexo)}>{anexo.nome}</button>
                                <span>{tamanhoArquivo(anexo.tamanho)}</span>
                                {pode && !feito && <button type="button" className="pr-link perigo" onClick={() => removerAnexo(anexo)}>remover</button>}
                            </div>
                        ))}
                        {pode && !feito && (
                            <label className="pr-enviar">
                                <CIcon name="cil-paperclip"/> Enviar arquivo
                                <input ref={arquivoRef} type="file" multiple onChange={e => enviarArquivos(e.target.files)}/>
                            </label>
                        )}
                    </section>

                    <section className="pr-bloco">
                        <div className="pr-bloco-titulo">Comentários e histórico</div>
                        <CTextarea rows={2} value={comentario} onChange={(e: any) => setComentario(e.target.value)} placeholder="Escreva um comentário"/>
                        <div className="pr-comentar">
                            <label className="pr-opcao pequena">
                                <input type="checkbox" checked={comentarioCliente} onChange={e => setComentarioCliente(e.target.checked)}/> visível ao cliente
                            </label>
                            <CButton size="sm" color="primary" variant="outline" disabled={!comentario.trim()} onClick={comentar}>Comentar</CButton>
                        </div>
                        <ol className="pr-linha-tempo">
                            {historico.map(h => (
                                <li key={h.id} className={`tipo-${h.tipo.toLowerCase()}`}>
                                    <span className="pr-linha-quando">{dataBr(h.data_hora)}</span>
                                    <span>{h.tipo === 'COMENTARIO' && h.dados?.mensagem ? `${h.usuario}: ${h.dados.mensagem}` : h.descricao}</span>
                                </li>
                            ))}
                        </ol>
                    </section>
                </div>
            </aside>
        </>
    );
};

export default ItemPainel;
